import * as React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import {
  useMessageTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
} from '@/hooks/useMessageTemplates';
import type { MessageTemplate } from '@/data/messageTemplates.service';
import { extractVariables } from '@/lib/templateUtils';
import { toast } from 'sonner';

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

export function ManageTemplatesSheet({ open, onOpenChange }: Props) {
  const { activeWorkspaceId } = useWorkspaceScope();
  const { data: templates = [], isLoading, refetch } = useMessageTemplates(undefined, {
    scopeType: 'keep_warm',
  });
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();

  const [name, setName] = React.useState('');
  const [body, setBody] = React.useState('');
  const [editing, setEditing] = React.useState<MessageTemplate | null>(null);

  React.useEffect(() => {
    if (!open) {
      setName('');
      setBody('');
      setEditing(null);
    }
  }, [open]);

  const startEdit = (t: MessageTemplate) => {
    setEditing(t);
    setName(t.name ?? '');
    setBody(t.body ?? '');
  };

  const reset = () => {
    setEditing(null);
    setName('');
    setBody('');
  };

  const onSave = async () => {
    if (!activeWorkspaceId) return;
    if (!name.trim() || !body.trim()) return;

    try {
      if (editing) {
        await updateTemplate.mutateAsync({
          id: editing.id,
          updates: {
            name: name.trim(),
            body: body.trim(),
            variables: extractVariables(body),
          },
        });
        toast.success('Template updated');
      } else {
        await createTemplate.mutateAsync({
          name: name.trim(),
          body: body.trim(),
          channel: 'whatsapp',
          category: 'reactivate',
          scope_type: 'keep_warm',
          variables: extractVariables(body),
        });
        toast.success('Template created');
      }
      reset();
      refetch();
    } catch (e: unknown) {
      toast.error((e as Error)?.message ?? 'Failed to save');
    }
  };

  const onDelete = async (t: MessageTemplate) => {
    if (t.scope_type === 'global') return;
    try {
      await deleteTemplate.mutateAsync(t.id);
      toast.success('Template deleted');
      refetch();
      if (editing?.id === t.id) reset();
    } catch (e: unknown) {
      toast.error((e as Error)?.message ?? 'Failed to delete');
    }
  };

  const saving = createTemplate.isPending || updateTemplate.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className={cn(
          'w-[520px] sm:w-[560px] p-0 gap-0',
          'bg-background/80 border-l border-border/60 backdrop-blur-xl shadow-2xl'
        )}
      >
        <div className="px-6 pt-6 pb-4">
          <SheetHeader className="space-y-1.5">
            <SheetTitle className="text-xl font-semibold text-foreground">
              Manage templates
            </SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">
              Create reusable WhatsApp templates. Use tokens like {'{first_name}'}, {'{company}'}.
            </SheetDescription>
          </SheetHeader>
        </div>
        <Separator className="bg-border/60" />

        <div className="grid grid-rows-[auto_1fr] h-[calc(100vh-80px)] min-h-0">
          <div className="p-4 space-y-3 border-b border-border/40">
            <div className="space-y-2">
              <Label htmlFor="sheet-template-name" className="text-foreground text-sm">
                Template name
              </Label>
              <Input
                id="sheet-template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Re-engage check-in"
                className="h-10 rounded-xl bg-muted/20 border-border/60 text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-0 transition-colors"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sheet-template-body" className="text-foreground text-sm">
                Template body
              </Label>
              <Textarea
                id="sheet-template-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Hi {first_name}, just checking in…"
                className="min-h-[120px] rounded-2xl resize-none bg-muted/20 border-border/60 text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-0 transition-colors"
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {editing ? 'Editing' : 'New template'} • scope: workspace
              </span>
              <div className="flex items-center gap-2">
                {editing ? (
                  <Button
                    variant="secondary"
                    onClick={reset}
                    disabled={saving}
                    className="rounded-xl bg-muted/25 border-border/60 text-foreground hover:bg-muted/40 transition-colors"
                  >
                    Cancel
                  </Button>
                ) : null}
                <Button
                  onClick={onSave}
                  disabled={saving || !activeWorkspaceId || !name.trim() || !body.trim()}
                  className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors"
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </div>

          <ScrollArea className="flex-1 min-h-0">
            <div className="p-4">
              {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : templates.length === 0 ? (
                <div className="py-8 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">No templates yet.</p>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setName('');
                      setBody('');
                      setEditing(null);
                    }}
                    className="rounded-xl bg-muted/25 border-border/60 text-foreground hover:bg-muted/40"
                  >
                    Create your first template
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map((t) => (
                    <div
                      key={t.id}
                      className={cn(
                        'rounded-xl border border-border/60 p-3 bg-card/30',
                        'hover:bg-card/50 hover:border-border/80 transition-all duration-200 ease-out'
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate text-foreground">{t.name}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[11px] text-muted-foreground">
                            {t.scope_type === 'global' ? 'GLOBAL' : 'WORKSPACE'}
                          </span>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="h-7 rounded-lg bg-muted/25 border-border/60 text-foreground hover:bg-muted/40 text-xs transition-colors"
                            onClick={() => startEdit(t)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="h-7 rounded-lg bg-muted/25 border-border/60 text-foreground hover:bg-muted/40 text-xs transition-colors disabled:opacity-50"
                            onClick={() => onDelete(t)}
                            disabled={t.scope_type === 'global'}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap line-clamp-3">
                        {t.body}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
}
